import {
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import { getMessagesFromRequest } from 'lib/api/chat';
import CustomButton from 'lib/designSystem/Button';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import { buildRequestId } from 'pages/Finance/Components/Financing/common/utils';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AuthState } from 'services/redux/authSlice';

import {
  ExportCheckboxesSetters,
  ExportCheckboxesState,
} from '../hooks/useExportCheckboxes';
import {
  BuildRequestOptions,
  buildSelectedRequest,
} from '../utils/buildRequest';
import { handleExportSimulation } from '../utils/exportSimulation';
import { ChatMessage, ChatMessagesDialog } from './ChatMessagesDialog';
import { ExportCheckboxes } from './ExportCheckboxes';

export type CustomBuildRequestFn = (
  selectedRequest: unknown,
  checkboxes: unknown,
) => unknown;

export type CustomOnExportFn = (request: unknown) => Promise<void>;

export interface BaseExportDialogProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  selectedRequest: unknown;
  operationDetails?: unknown;
  checkboxes: ExportCheckboxesState | unknown;
  setters: ExportCheckboxesSetters | unknown;
  buildRequestOptions?: Omit<
    BuildRequestOptions,
    'selectedRequest' | 'operationDetails' | 'checkboxes'
  >;
  exportOptions?: {
    includePayouts?: boolean;
    canShowPayouts?: boolean;
  };
  includeRateComposition?: boolean;
  customCheckboxes?: React.ReactNode;
  customOnExport?: CustomOnExportFn;
  customBuildRequest?: CustomBuildRequestFn;
  dialogMaxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | false;
  dialogFullWidth?: boolean;
  chatRequestType?: string;
  externalLoadingChatMessages?: boolean;
}

export const BaseExportDialog: React.FC<BaseExportDialogProps> = ({
  open,
  setOpen,
  selectedRequest,
  operationDetails,
  checkboxes,
  setters,
  buildRequestOptions = {},
  exportOptions = {},
  includeRateComposition = false,
  customCheckboxes,
  customOnExport,
  customBuildRequest,
  dialogMaxWidth,
  dialogFullWidth = false,
  chatRequestType = 'factoring-request',
  externalLoadingChatMessages,
}) => {
  const [loading, setLoading] = useState(false);
  const [internalLoadingChatMessages, setInternalLoadingChatMessages] =
    useState(false);
  const [chatDialogOpen, setChatDialogOpen] = useState(false);
  const dispatch = useDispatch();
  const user = useSelector((state: { auth: AuthState }) => state.auth.user);

  // Usar el loading externo si se proporciona, sino usar el interno
  const loadingChatMessages =
    externalLoadingChatMessages !== undefined
      ? externalLoadingChatMessages
      : internalLoadingChatMessages;

  const { includePayouts = false, canShowPayouts = false } = exportOptions;

  const handleChatButtonClick = () => {
    setChatDialogOpen(true);
  };

  const handleChatSelectionChange = (selectedMessages: ChatMessage[]) => {
    // Guardar los IDs de los mensajes seleccionados
    const selectedIds = selectedMessages.map((msg) => msg.id);
    const settersTyped = setters as ExportCheckboxesSetters;
    if (settersTyped.setSelectedChatMessages) {
      settersTyped.setSelectedChatMessages(selectedIds);
    }
    // Almacenar los objetos completos para usarlos en buildRequest
    if (settersTyped.setSelectedChatMessagesObjects) {
      settersTyped.setSelectedChatMessagesObjects(selectedMessages);
    }
  };

  const handleChatAsObservationsChange = async (checked: boolean) => {
    const settersTyped = setters as ExportCheckboxesSetters;
    if (settersTyped.setShowChatAsObservations) {
      settersTyped.setShowChatAsObservations(checked);
    }

    if (checked) {
      const checkboxesTyped = checkboxes as ExportCheckboxesState;
      const hasExistingSelection =
        checkboxesTyped.selectedChatMessages?.length > 0 &&
        checkboxesTyped.selectedChatMessagesObjects?.length > 0;

      if (!hasExistingSelection) {
        if (externalLoadingChatMessages === undefined) {
          setInternalLoadingChatMessages(true);
        }
        try {
          const requestId = buildRequestId(
            selectedRequest as Record<string, unknown>,
            chatRequestType,
          );
          const apiRequestType =
            chatRequestType === 'confirming-request'
              ? 'confirming'
              : 'factoring';
          const response = await getMessagesFromRequest(dispatch, {
            requestType: apiRequestType,
            requestId: Number(requestId),
          });

          if (response?.messages && response.messages.length > 0) {
            // Mapear mensajes de la API al formato esperado
            const mappedMessages: ChatMessage[] = response.messages.map(
              (
                msg: {
                  id?: string;
                  userId?: number;
                  text?: string;
                  userName?: string;
                  userRole?: string;
                  timestamp?: string | number | Date;
                },
                index: number,
              ) => ({
                id: msg.id || `msg-${index}-${msg.timestamp}`,
                userId: msg.userId || 0,
                text: msg.text || '',
                userName: msg.userName || 'Usuario',
                userRole: msg.userRole || '',
                timestamp: msg.timestamp ? new Date(msg.timestamp) : new Date(),
              }),
            );

            // Seleccionar todos los mensajes por defecto solo si no hay selección previa
            const allIds = mappedMessages.map((msg) => msg.id);
            if (settersTyped.setSelectedChatMessages) {
              settersTyped.setSelectedChatMessages(allIds);
            }
            if (settersTyped.setSelectedChatMessagesObjects) {
              settersTyped.setSelectedChatMessagesObjects(mappedMessages);
            }
          } else {
            // Si no hay mensajes, limpiar la selección
            if (settersTyped.setSelectedChatMessages) {
              settersTyped.setSelectedChatMessages([]);
            }
            if (settersTyped.setSelectedChatMessagesObjects) {
              settersTyped.setSelectedChatMessagesObjects([]);
            }
          }
        } catch (error) {
          console.error('Error loading chat messages:', error);
          // En caso de error, limpiar la selección
          if (settersTyped.setSelectedChatMessages) {
            settersTyped.setSelectedChatMessages([]);
          }
          if (settersTyped.setSelectedChatMessagesObjects) {
            settersTyped.setSelectedChatMessagesObjects([]);
          }
        } finally {
          if (externalLoadingChatMessages === undefined) {
            setInternalLoadingChatMessages(false);
          }
        }
      }
    } else {
      // Si se desmarca, limpiar la selección
      if (settersTyped.setSelectedChatMessages) {
        settersTyped.setSelectedChatMessages([]);
      }
      if (settersTyped.setSelectedChatMessagesObjects) {
        settersTyped.setSelectedChatMessagesObjects([]);
      }
      if (externalLoadingChatMessages === undefined) {
        setInternalLoadingChatMessages(false);
      }
    }
  };

  const onExport = async () => {
    setLoading(true);
    try {
      let request;
      if (customBuildRequest) {
        request = customBuildRequest(selectedRequest, checkboxes);
      } else {
        request = buildSelectedRequest({
          selectedRequest,
          operationDetails,
          checkboxes: checkboxes as ExportCheckboxesState,
          ...buildRequestOptions,
        });
      }

      if (customOnExport) {
        await customOnExport(request);
      } else {
        await handleExportSimulation({
          dispatch,
          user,
          request,
          checkboxes: checkboxes as ExportCheckboxesState,
          includePayouts,
          canShowPayouts,
        });
      }

      setOpen(false);
    } catch (error) {
      // Error ya manejado en handleExportSimulation o customOnExport
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => setOpen(false)}
      maxWidth={dialogMaxWidth}
      fullWidth={dialogFullWidth}
    >
      <DialogTitle>
        Exportar simulación solicitud{' '}
        <strong>
          #{(selectedRequest as Record<string, unknown>).id as number}
        </strong>
      </DialogTitle>
      <DialogContent>
        {loading && (
          <div style={{ minHeight: 200, minWidth: 500 }}>
            <CenteredCircularProgress type="dialog" />
          </div>
        )}
        {!loading && (
          <div>
            <Typography>
              {customCheckboxes
                ? 'Selecciona qué elementos incluir en el documento PDF.'
                : 'Seleccione los datos que desea incluir/ocultar en la simulación.'}
            </Typography>
            {loadingChatMessages && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  marginTop: '8px',
                  marginBottom: '8px',
                }}
              >
                <CircularProgress size={16} />
                <Typography variant="body2" color="text.secondary">
                  Cargando mensajes del chat...
                </Typography>
              </div>
            )}
            {customCheckboxes ? (
              customCheckboxes
            ) : (
              <ExportCheckboxes
                checkboxes={checkboxes as ExportCheckboxesState}
                setters={setters as ExportCheckboxesSetters}
                selectedRequest={selectedRequest as Record<string, unknown>}
                canShowPayouts={canShowPayouts}
                includeRateComposition={includeRateComposition}
                includePayouts={includePayouts}
                onChatButtonClick={handleChatButtonClick}
                onChatAsObservationsChange={(checked) => {
                  void handleChatAsObservationsChange(checked);
                }}
                isChatButtonDisabled={
                  !(checkboxes as ExportCheckboxesState)?.showChatAsObservations
                }
              />
            )}
          </div>
        )}
      </DialogContent>
      <DialogActions>
        <CustomButton color="secondary" onClick={() => setOpen(false)}>
          Cancelar
        </CustomButton>
        <CustomButton
          color="primary"
          onClick={onExport}
          loading={loadingChatMessages}
        >
          Exportar
        </CustomButton>
      </DialogActions>
      {(checkboxes as ExportCheckboxesState).selectedChatMessages && (
        <ChatMessagesDialog
          open={chatDialogOpen}
          onClose={() => setChatDialogOpen(false)}
          selectedRequest={selectedRequest}
          requestType={
            chatRequestType === 'confirming-request'
              ? 'confirming'
              : 'factoring'
          }
          selectedMessages={
            (checkboxes as ExportCheckboxesState).selectedChatMessages || []
          }
          onSelectionChange={handleChatSelectionChange}
        />
      )}
    </Dialog>
  );
};
